import { useState, useEffect, useCallback } from 'react'
import { getFirAuth } from '../../Api/firebase/firebaseConnect'
import { handleAuthError, logEvent } from '../../Utils'
import { ANALYTICS_TAGS, PHONE_CONSOLE_MESSAGES } from '../../Constant'
import type { RecaptchaVerifier } from './types'

/**
 * Initializes phone verification reCAPTCHA
 *
 * @param verifierId The ID of the HTML element to bind the verifier to.
 * @param shouldInitialize Whether reCAPTCHA should be initialized (only when phone input is shown)
 * @returns The RecaptchaVerifier instance or null
 */
export const useRecaptcha = (
  verifierId: string,
  shouldInitialize: boolean = true
): RecaptchaVerifier | null => {
  const [verifier, setVerifier] = useState<RecaptchaVerifier | null>(null)

  const checkElementAndInitialize = useCallback(async () => {
    try {
      const auth = await getFirAuth()
      if (!auth) {
        console.warn(PHONE_CONSOLE_MESSAGES.RECAPTCHA_AUTH_UNAVAILABLE)
        handleAuthError(
          ANALYTICS_TAGS.auth_change_error,
          'Auth instance not provided for reCAPTCHA verifier',
          'useRecaptcha'
        )
        return null
      }

      const element = document.getElementById(verifierId)
      if (!element) {
        console.warn(
          `${PHONE_CONSOLE_MESSAGES.RECAPTCHA_ELEMENT_NOT_FOUND} "${verifierId}" not found during initialization.`
        )
        return null
      }

      console.log(
        `${PHONE_CONSOLE_MESSAGES.RECAPTCHA_FOUND_INITIALIZING} "${verifierId}" - initializing reCAPTCHA`
      )

      const { RecaptchaVerifier: FirebaseRecaptchaVerifier } = await import(
        /* webpackChunkName: "firebase-auth" */ 'firebase/auth'
      )

      const recaptchaVerifier = new FirebaseRecaptchaVerifier(auth, verifierId, {
        size: 'invisible',
        callback: () => {
        },
        'expired-callback': () => {
          console.warn('reCAPTCHA expired - user may need to retry')
          logEvent(ANALYTICS_TAGS.recaptcha_expired, {
            element: verifierId,
            product: 'web',
          })
        },
        'error-callback': (error: any) => {
          console.error(PHONE_CONSOLE_MESSAGES.RECAPTCHA_INIT_FAILED, error)
          handleAuthError(
            ANALYTICS_TAGS.recaptcha_error,
            error,
            'useRecaptcha_error_callback'
          )
        },
      }) as unknown as RecaptchaVerifier

      return recaptchaVerifier
    } catch (error) {
      console.error(PHONE_CONSOLE_MESSAGES.RECAPTCHA_INIT_FAILED, error)
      handleAuthError(ANALYTICS_TAGS.auth_change_error, error, 'useRecaptcha')
      return null
    }
  }, [verifierId])

  useEffect(() => {
    if (!shouldInitialize || verifier) {
      return
    }

    let recaptchaVerifier: RecaptchaVerifier | null = null
    let retryCount = 0
    const maxRetries = 10
    const retryDelay = 500

    const initializeWithRetry = async () => {
      const verifierInstance = await checkElementAndInitialize()

      if (verifierInstance) {
        recaptchaVerifier = verifierInstance
        setVerifier(recaptchaVerifier)
        return
      }

      if (retryCount < maxRetries) {
        retryCount++
        setTimeout(initializeWithRetry, retryDelay)
        return
      }

      // Log failure after max retries
      logEvent(ANALYTICS_TAGS.recaptcha_init_failed, {
        element: verifierId,
        maxRetries,
        product: 'web',
      })
    }

    initializeWithRetry()

    return () => {
      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear()
        } catch (error) {
          console.error(PHONE_CONSOLE_MESSAGES.RECAPTCHA_CLEAR_ERROR, error)
        }
      }
    }
  }, [verifierId, shouldInitialize, checkElementAndInitialize, verifier])

  useEffect(() => {
    if (!shouldInitialize && verifier) {
      try {
        verifier.clear()
        setVerifier(null)
      } catch (error) {
        console.error(PHONE_CONSOLE_MESSAGES.RECAPTCHA_CLEAR_ERROR, error)
      }
    }
  }, [shouldInitialize, verifier])

  return verifier
}
